export const swapPairs = [
  {
    pair: 'EURUSD',
    long: -0.48,
    short: 0.12,
    label: 'Major',
    description: 'Typical for a major pair — long positions pay small swap, short earns tiny credit.',
  },
  {
    pair: 'GBPJPY',
    long: 0.82,
    short: -1.35,
    label: 'Cross',
    description: 'High interest rate differential — large swap costs on one side, decent credit on the other.',
  },
  {
    pair: 'USDTRY',
    long: -28.50,
    short: 12.40,
    label: 'Exotic',
    description: 'Extreme rate differential — swap dwarfs spread and commission combined on exotic pairs.',
  },
  {
    pair: 'XAUUSD',
    long: -0.35,
    short: -0.25,
    label: 'Commodity',
    description: 'Gold positions have unique swap dynamics — both sides typically pay.',
  },
  {
    pair: 'AUDUSD',
    long: -0.22,
    short: 0.55,
    label: 'Major',
    description: 'AUD rate advantage — short positions earn relatively high credit due to RBA rates.',
  },
] as const;

export const swapScenarios = [
  {
    label: 'Day Trader',
    tradesPerMonth: 100,
    lotSize: 0.5,
    avgHoldDays: 0,
    description: 'Positions closed same day — swap is irrelevant.',
  },
  {
    label: 'Swing Trader',
    tradesPerMonth: 20,
    lotSize: 2.0,
    avgHoldDays: 5,
    description: 'Positions held for days — swap starts to matter significantly.',
  },
  {
    label: 'Position Trader',
    tradesPerMonth: 5,
    lotSize: 3.0,
    avgHoldDays: 30,
    description: 'Positions held for weeks or months — swap can be the largest cost.',
  },
] as const;

export const swapImpact = [
  {
    pair: 'EURUSD',
    swapLong: -0.48,
    swapShort: 0.12,
    holdDays: 10,
    lotSize: 1.0,
    tagline: 'Swap is minor for majors — spread and commission dominate.',
  },
  {
    pair: 'GBPJPY',
    swapLong: 0.82,
    swapShort: -1.35,
    holdDays: 10,
    lotSize: 1.0,
    tagline: 'Swap can exceed spread — holding direction matters a lot on crosses.',
  },
  {
    pair: 'USDTRY',
    swapLong: -28.50,
    swapShort: 12.40,
    holdDays: 10,
    lotSize: 1.0,
    tagline: 'Swap IS the cost — spread and commission are rounding errors on exotics.',
  },
] as const;

export const swapFAQs = [
  {
    question: 'What is forex swap (rollover)?',
    answer: 'Swap — also called rollover or overnight financing — is the interest you pay or earn for holding a forex position past 5 PM ET (New York close). It exists because forex is traded on leverage: you are effectively borrowing one currency to buy another. The swap rate reflects the interest rate difference between the two currencies in the pair. If you buy a higher-yielding currency, you may earn swap. If you buy a lower-yielding one, you pay swap.',
    category: 'Basics',
  },
  {
    question: 'Why is swap sometimes a credit and sometimes a charge?',
    answer: 'Swap direction depends on which currency you bought vs sold. If you go long EURUSD (buy EUR, sell USD) and EUR has a higher interest rate than USD, you earn swap credit. If EUR\'s rate is lower, you pay. The same pair can have positive swap on one side and negative on the other. For example, long GBPJPY might earn +0.82 pips/day while short GBPJPY pays -1.35 pips/day — the difference is the broker\'s markup on the rate spread.',
    category: 'Basics',
  },
  {
    question: 'What is triple swap Wednesday?',
    answer: 'Forex settles T+2, meaning a trade opened Wednesday would normally settle Friday. But since markets are closed on weekends, Wednesday\'s rollover includes three days of interest: Wednesday + Saturday + Sunday. This means positions held through Wednesday 5 PM ET pay or earn 3× the normal swap rate. This applies whether you are long or short. Some brokers apply triple swap on Thursday instead — check your broker\'s specific policy.',
    category: 'Basics',
  },
  {
    question: 'How do I calculate swap cost?',
    answer: 'Swap cost = swap rate in pips × pip value × lot size × number of days held. For a 1.0 lot long GBPJPY position held 10 days with -1.35 pips/day swap: -1.35 × ¥1,000 (pip value) × 1.0 lot × 10 days = -¥13,500, or about -$90 USD. Swap rates are quoted in the quote currency — for GBPJPY, the swap is in JPY, which must be converted to your account currency. Always confirm the pip value and swap rate in your broker\'s contract specifications.',
    category: 'Calculation',
  },
  {
    question: 'Do all brokers charge the same swap rates?',
    answer: 'No — swap rates vary significantly between brokers. The base rate is the interbank tom-next rate, but brokers add a markup. A typical markup is 0.2–0.5 pips on each side. ECN brokers usually charge lower markups, while market makers may add 0.5–1.0 pip or more. Additionally, brokers use different benchmark rates — some update swap rates daily based on overnight LIBOR/SOFR rates, others use fixed weekly rates. Always compare swap rates when choosing a broker for swing or position trading.',
    category: 'Broker Differences',
  },
  {
    question: 'Which pairs have the highest swap rates?',
    answer: 'Emerging market pairs have the highest swap rates due to extreme interest rate differentials. USDTRY (Turkish Lira) can have swap rates of 20–40 pips/day because Turkey\'s benchmark rate is far above the US rate. USDMXN, USDZAR, and EURHUF also have large swaps. Among majors, GBPJPY and AUDJPY typically have the widest swap spreads. At the low end, EURUSD and USDCHF swaps are usually under 1 pip/day because rate differentials are narrow.',
    category: 'Pair Differences',
  },
  {
    question: 'What are Islamic (swap-free) accounts?',
    answer: 'Islamic accounts eliminate swap charges entirely — both paying and earning overnight interest — to comply with Sharia law which prohibits interest (riba). Instead, brokers typically charge a fixed admin fee (e.g., $5–$10 per lot per week) or widen the spread slightly. Some brokers offer truly swap-free accounts for a limited number of days (7–14), after which admin fees apply. Not all brokers offer Islamic accounts, and those that do often require documentation to verify eligibility.',
    category: 'Account Types',
  },
  {
    question: 'Can I avoid swap costs entirely?',
    answer: 'Only by closing all positions before 5 PM ET each day (day trading) or by using a swap-free Islamic account. Day traders never hold overnight, so swap is irrelevant to them. Swing and position traders, however, must accept swap as a cost of doing business. Some traders try to only trade in the swap-positive direction (e.g., only short AUDUSD to earn credit), but this limits strategy. A practical approach: include swap in your trade plan from the start, not as an afterthought.',
    category: 'Trading Strategy',
  },
  {
    question: 'How does swap affect carry trade strategies?',
    answer: 'Carry trades explicitly rely on swap — you go long a high-yielding currency and short a low-yielding one to earn the daily interest differential. Classic carry pairs include long AUDJPY, NZDJPY, and USDTRY. The profit comes from swap credits accumulating daily, not from price movement. However, carry trades carry significant risk: a sudden currency depreciation can wipe out months of swap earnings in hours. The 2008 financial crisis destroyed many carry trade portfolios when the yen surged.',
    category: 'Trading Strategy',
  },
  {
    question: 'Where do I find swap rates on my trading platform?',
    answer: 'In MT4/MT5: right-click any instrument in Market Watch → Specification → scroll to "Swap Long" and "Swap Short." In cTrader: open the instrument\'s details panel → Trading Conditions → Swaps. On broker websites: look for "Contract Specifications" or "Trading Conditions" — most reputable brokers publish a full swap table. Swap rates are usually shown in points (not pips) on MT4/MT5 — divide by 10 to convert to pips. Rates update daily at 5 PM ET.',
    category: 'Practical',
  },
  {
    question: 'Does swap apply to indices and commodities too?',
    answer: 'Yes — CFDs on indices (S&P 500, FTSE 100, DAX 40) and commodities (XAUUSD, USOIL) also have overnight financing charges. The calculation differs from forex: for indices, swap is typically a percentage of the notional value per day. For commodities like gold, it is a fixed dollar amount per lot. Cryptocurrency CFDs often have much higher swap rates — 0.05%–0.10% per day is common, which compounds to 18–36% annually. Always check the specific instrument\'s specifications.',
    category: 'Beyond Forex',
  },
  {
    question: 'How do central bank rate decisions affect swap?',
    answer: 'When a central bank changes its benchmark rate, swap rates adjust — usually within 1–2 business days. A rate hike in the currency you are long increases swap credit (or reduces what you pay). A rate cut reduces it. Major events like FOMC, ECB, BOE, and BOJ decisions are critical for swing traders because a single 50bp rate change can significantly alter the daily swap cost on open positions. Smart traders check the economic calendar for rate decisions when planning multi-week trades.',
    category: 'Trading Strategy',
  },
] as const;

export const relatedSwapGuides = [
  {
    title: 'Spread',
    href: '/forex-spread/',
    desc: 'The moment you open a trade, you\'re already down by the spread. See what determines its width and when it\'s cheapest to trade.',
    icon: 'ri-arrow-left-right-line',
  },
  {
    title: 'Commission',
    href: '/forex-commission/',
    desc: 'ECN brokers charge a flat fee per lot — it sounds scary but often works out cheaper than commission-free. Here\'s the math.',
    icon: 'ri-bank-card-line',
  },
  {
    title: 'Slippage',
    href: '/forex-slippage/',
    desc: 'News events, low liquidity, big orders — any of these can make your fill worse than expected. Learn what triggers slippage.',
    icon: 'ri-speed-up-line',
  },
  {
    title: 'Hidden Charges',
    href: '/hidden-forex-trading-costs/',
    desc: 'Conversion fees, withdrawal charges, idle account penalties — the stuff brokers bury in fine print but your wallet definitely notices.',
    icon: 'ri-eye-off-line',
  },
] as const;

// ═══════════════════════════════════════════════════════════
// Triple Swap Wednesday Rule
// ═══════════════════════════════════════════════════════════

export const tripleSwapPairs = [
  {
    pair: 'EURUSD',
    swapLong: -0.48,
    swapShort: 0.12,
    pipVal: 10,
    label: 'Major',
    normalDayLongCost: -4.80,
    normalDayShortCost: 1.20,
    wednesdayLongCost: -14.40,
    wednesdayShortCost: 3.60,
  },
  {
    pair: 'GBPJPY',
    swapLong: 0.82,
    swapShort: -1.35,
    pipVal: 7.5,
    label: 'Cross',
    normalDayLongCost: 6.15,
    normalDayShortCost: -10.13,
    wednesdayLongCost: 18.45,
    wednesdayShortCost: -30.38,
  },
  {
    pair: 'USDTRY',
    swapLong: -28.50,
    swapShort: 12.40,
    pipVal: 8,
    label: 'Exotic',
    normalDayLongCost: -228.00,
    normalDayShortCost: 99.20,
    wednesdayLongCost: -684.00,
    wednesdayShortCost: 297.60,
  },
] as const;

export const weeklyCalendar = [
  { day: 'Monday', label: 'Mon', swapMultiplier: 1, desc: 'Normal swap — 1 day of interest' },
  { day: 'Tuesday', label: 'Tue', swapMultiplier: 1, desc: 'Normal swap — 1 day of interest' },
  { day: 'Wednesday', label: 'Wed', swapMultiplier: 3, desc: 'Triple swap — covers Wed + Sat + Sun (3 days)' },
  { day: 'Thursday', label: 'Thu', swapMultiplier: 1, desc: 'Normal swap — 1 day of interest' },
  { day: 'Friday', label: 'Fri', swapMultiplier: 1, desc: 'Normal swap — 1 day of interest (no weekend charge on Fri)' },
] as const;

// ═══════════════════════════════════════════════════════════
// Broker Swap Rate Comparison
// ═══════════════════════════════════════════════════════════

export const brokerSwapRates = [
  {
    broker: 'ECN Broker A',
    type: 'ECN / Raw Spread',
    markup: '~0.2 pip markup',
    philosophy: 'Passes interbank tom-next rates with minimal spread. Best for high-volume swing traders who hold positions across multiple sessions.',
    pairs: {
      EURUSD: { long: -0.42, short: 0.18 },
      GBPJPY: { long: 0.95, short: -1.20 },
      USDTRY: { long: -25.00, short: 14.50 },
    },
  },
  {
    broker: 'Standard Broker B',
    type: 'Standard / No Commission',
    markup: '~0.5 pip markup',
    philosophy: 'Mid-range rates typical of most retail brokers. Commission-free model offsets with slightly wider swap spreads. The default choice for most traders.',
    pairs: {
      EURUSD: { long: -0.55, short: 0.06 },
      GBPJPY: { long: 0.70, short: -1.50 },
      USDTRY: { long: -30.00, short: 11.00 },
    },
  },
  {
    broker: 'Market Maker C',
    type: 'Market Maker',
    markup: '~1.0 pip markup',
    philosophy: 'Fixed spreads come at a cost — swap rates carry the widest markups. Position traders and carry trade strategies are hit hardest by the accumulated difference.',
    pairs: {
      EURUSD: { long: -0.72, short: -0.15 },
      GBPJPY: { long: 0.40, short: -1.90 },
      USDTRY: { long: -38.00, short: 8.00 },
    },
  },
] as const;